import { useState, useEffect, useCallback } from "react";
import {
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TableCell,
  TableRow,
  Chip,
  Paper,
} from "@mui/material";
import SbuxDialog2 from "../../../../../components/SbuxDialog2";
import SbuxSelect from "../../../../../components/SbuxSelect";
import SbuxLoader from "../../../../../components/SbuxLoader";
import SbuxTranslate from "../../../../../components/SbuxTranslate";
import useCss from "../../../../../hooks/useCss";
import styles from "./styles";
import LexSection from "./components/LexSection";
import LexDataTable from "./components/LexDataTable";
import type { LexBotInfo } from "./types";
import { useAppDispatch, useAppSelector } from "../../../../../store/types";
import {
  getLexIntents,
  getLexUtterances,
  getLexSlots,
  getLexSlotTypes,
  getLexSlotTypeDetail,
  getLexMissedUtterances,
  resetLexBotDetail,
} from "../../../../../services/lexBots";
import {
  getLexIntentsSelector,
  getLexIntentsLoadingSelector,
  getLexUtterancesSelector,
  getLexUtterancesLoadingSelector,
  getLexSlotsSelector,
  getLexSlotsLoadingSelector,
  getLexSlotTypesSelector,
  getLexSlotTypesLoadingSelector,
  getLexSlotTypeDetailSelector,
  getLexSlotTypeDetailLoadingSelector,
  getLexSlotTypeDetailErrorSelector,
  getLexMissedUtterancesSelector,
  getLexMissedUtterancesLoadingSelector,
  getLexMissedUtterancesErrorSelector,
} from "../../../../../selectors/lexBotsSelector";

const INTENT_SELECT_MAX_HEIGHT = 300;
const EMPTY_UTTERANCE_SENTINEL = "<empty_utterances>";

interface Props {
  open: boolean;
  bot: LexBotInfo | null;
  onClose: () => void;
}

const LexBotDetailModal = ({ open, bot, onClose }: Props) => {
  const classes = useCss(styles);
  const dispatch = useAppDispatch();

  // Local UI state
  const [selectedIntentId, setSelectedIntentId] = useState("");
  const [selectedSlotTypeId, setSelectedSlotTypeId] = useState("");

  // Redux selectors
  const intents = useAppSelector(getLexIntentsSelector);
  const intentsLoading = useAppSelector(getLexIntentsLoadingSelector);
  const utterances = useAppSelector(getLexUtterancesSelector);
  const utterancesLoading = useAppSelector(getLexUtterancesLoadingSelector);
  const slots = useAppSelector(getLexSlotsSelector);
  const slotsLoading = useAppSelector(getLexSlotsLoadingSelector);
  const slotTypes = useAppSelector(getLexSlotTypesSelector);
  const slotTypesLoading = useAppSelector(getLexSlotTypesLoadingSelector);
  const slotTypeDetail = useAppSelector(getLexSlotTypeDetailSelector);
  const slotTypeDetailLoading = useAppSelector(
    getLexSlotTypeDetailLoadingSelector,
  );
  const slotTypeDetailError = useAppSelector(getLexSlotTypeDetailErrorSelector);
  const missedUtterances = useAppSelector(getLexMissedUtterancesSelector);
  const missedUtterancesLoading = useAppSelector(
    getLexMissedUtterancesLoadingSelector,
  );
  const missedUtterancesError = useAppSelector(
    getLexMissedUtterancesErrorSelector,
  );

  // On bot change: fire parallel calls for intents, slotTypes, missedUtterances
  useEffect(() => {
    if (!bot || !open) return;
    dispatch(resetLexBotDetail());
    const { botId, botVersion, localeId, botAliasId } = bot;
    if (!botId || !botVersion || !localeId) return;

    dispatch(getLexIntents({ botId, botVersion, localeId }));
    dispatch(getLexSlotTypes({ botId, botVersion, localeId }));
    if (botAliasId) {
      dispatch(
        getLexMissedUtterances({ botId, botAliasId, localeId, botVersion }),
      );
    }
  }, [
    bot?.botId,
    bot?.botVersion,
    bot?.localeId,
    bot?.botAliasId,
    open,
    dispatch,
  ]);

  // Auto-select first intent when intents load
  useEffect(() => {
    if (intents.length > 0) {
      setSelectedIntentId(intents[0].intentId ?? "");
    } else {
      setSelectedIntentId("");
    }
  }, [intents]);

  // On intent selection: fire utterances + slots
  useEffect(() => {
    if (!bot || !selectedIntentId) return;
    const { botId, botVersion, localeId } = bot;
    if (!botId || !botVersion || !localeId) return;

    dispatch(
      getLexUtterances({
        botId,
        botVersion,
        localeId,
        intentId: selectedIntentId,
      }),
    );
    dispatch(
      getLexSlots({ botId, botVersion, localeId, intentId: selectedIntentId }),
    );
  }, [selectedIntentId, bot?.botId, bot?.botVersion, bot?.localeId, dispatch]);

  // Auto-select first slot type when slotTypes load
  useEffect(() => {
    if (slotTypes.length > 0) {
      setSelectedSlotTypeId(slotTypes[0].slotTypeId ?? "");
    } else {
      setSelectedSlotTypeId("");
    }
  }, [slotTypes]);

  // On slot type selection: fire slot type detail
  useEffect(() => {
    if (!bot || !selectedSlotTypeId) return;
    const { botId, botVersion, localeId } = bot;
    if (!botId || !botVersion || !localeId) return;

    dispatch(
      getLexSlotTypeDetail({
        botId,
        botVersion,
        localeId,
        slotTypeId: selectedSlotTypeId,
      }),
    );
  }, [
    selectedSlotTypeId,
    bot?.botId,
    bot?.botVersion,
    bot?.localeId,
    dispatch,
  ]);

  // Close handler
  const handleClose = useCallback(() => {
    dispatch(resetLexBotDetail());
    setSelectedIntentId("");
    setSelectedSlotTypeId("");
    onClose();
  }, [dispatch, onClose]);

  if (!bot) return null;

  // Summary fields
  const summaryFields: { label: string; value: string }[] = [
    { label: "Bot Name", value: bot.botName ?? "-" },
    { label: "Alias", value: bot.botAliasName ?? "-" },
    { label: "Version", value: bot.botVersion ?? "-" },
    { label: "Language", value: bot.localeId ?? "-" },
  ];

  // Intent dropdown items
  const intentMenuItems = intents.map((i) => ({
    value: i.intentId ?? "",
    name: i.intentName ?? i.intentId ?? "",
  }));

  const dialogConfig = {
    title: <SbuxTranslate>{"Lex Bot Details"}</SbuxTranslate>,
    fullWidth: true,
    asModal: true,
    disableBackdropClose: false,
  };
  return (
    <SbuxDialog2 config={dialogConfig} open={open} handleOnClose={handleClose}>
      <DialogContent dividers>
        <Box className={classes.modalContent}>
          {/* Section 1: Bot Summary */}
          <LexSection title="Bot Summary">
            <Box className={classes.summaryGrid}>
              {summaryFields.map(({ label, value }) => (
                <Box key={label} className={classes.summaryItem}>
                  <Typography className={classes.summaryKey}>
                    <SbuxTranslate>{label}</SbuxTranslate>
                  </Typography>
                  <Typography className={classes.summaryValue}>
                    {value}
                  </Typography>
                </Box>
              ))}
            </Box>
          </LexSection>

          {/* Section 2: Intent */}
          <LexSection title="Intent">
            {intentsLoading ? (
              <SbuxLoader />
            ) : (
              <SbuxSelect
                label={<SbuxTranslate>{"Select Intent"}</SbuxTranslate>}
                menuItems={intentMenuItems}
                value={selectedIntentId}
                handleSelectChange={(val) => setSelectedIntentId(val)}
                disabled={intents.length === 0}
                menuMaxHeight={INTENT_SELECT_MAX_HEIGHT}
              />
            )}
          </LexSection>

          {/* Section 3: Utterances */}
          <LexSection title="Utterances">
            {utterancesLoading ? (
              <SbuxLoader />
            ) : utterances.length === 0 ? (
              <Typography className={classes.emptyText}>
                <SbuxTranslate>
                  {"No utterances configured for this intent."}
                </SbuxTranslate>
              </Typography>
            ) : (
              <Paper variant="outlined" className={classes.scrollableList}>
                {utterances.map((u, i) => (
                  <Typography
                    key={`${i}-${u}`}
                    className={classes.utteranceLine}
                  >
                    {u}
                  </Typography>
                ))}
              </Paper>
            )}
          </LexSection>

          {/* Section 4: Slots (intent-scoped) */}
          <LexSection title="Slots" annotation=" (intent-scoped)">
            {slotsLoading ? (
              <SbuxLoader />
            ) : slots.length === 0 ? (
              <Typography className={classes.emptyText}>
                <SbuxTranslate>
                  {"No slots configured for this intent."}
                </SbuxTranslate>
              </Typography>
            ) : (
              <LexDataTable
                containerClass={classes.compactTableContainer}
                columns={["Slot Name", "Elicitation Prompts"]}
              >
                {slots.map((slot) => (
                  <TableRow key={slot.slotId} hover>
                    <TableCell className={classes.compactTableCell}>
                      {slot.slotName}
                    </TableCell>
                    <TableCell className={classes.compactTableCell}>
                      <Box className={classes.promptList}>
                        {(slot.elicitationPromptTexts ?? []).map((p, i) => (
                          <Chip
                            key={i}
                            label={p}
                            size="small"
                            variant="outlined"
                            className={classes.promptChip}
                          />
                        ))}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </LexDataTable>
            )}
          </LexSection>

          {/* Section 5: Slot Types (locale-scoped, independent) */}
          <LexSection
            title="Slot Types"
            annotation=" (locale-scoped - click a row to view its values below)"
          >
            {slotTypesLoading ? (
              <SbuxLoader />
            ) : slotTypes.length === 0 ? (
              <Typography className={classes.emptyText}>
                <SbuxTranslate>
                  {"No slot types found for this locale."}
                </SbuxTranslate>
              </Typography>
            ) : (
              <LexDataTable
                containerClass={classes.slotTypesTableContainer}
                columns={["Slot Type Name"]}
              >
                {slotTypes.map((st) => {
                  const isSelected = st.slotTypeId === selectedSlotTypeId;
                  return (
                    <TableRow
                      key={st.slotTypeId}
                      hover
                      selected={isSelected}
                      onClick={() => setSelectedSlotTypeId(st.slotTypeId ?? "")}
                      className={classes.clickableRow}
                    >
                      <TableCell className={classes.compactTableCell}>
                        {st.slotTypeName}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </LexDataTable>
            )}
          </LexSection>

          {/* Section 6: Slot Type Values */}
          <LexSection title="Slot Type Values">
            {slotTypeDetailLoading ? (
              <SbuxLoader />
            ) : slotTypeDetailError ? (
              <Typography className={classes.emptyText}>
                <SbuxTranslate>
                  {"Failed to load slot type values."}
                </SbuxTranslate>
              </Typography>
            ) : !slotTypeDetail ? (
              <Typography className={classes.emptyText}>
                <SbuxTranslate>
                  {"Select a slot type above to view its values."}
                </SbuxTranslate>
              </Typography>
            ) : (
              <>
                <Box>
                  <Typography className={classes.summaryKey} sx={{ mb: 0.5 }}>
                    {slotTypeDetail.slotTypeName}
                  </Typography>
                  {slotTypeDetail.valueType && (
                    <Box className={classes.valueTypeBadge}>
                      {slotTypeDetail.valueType}
                    </Box>
                  )}
                </Box>
                {(slotTypeDetail.slotTypeValues?.length ?? 0) === 0 ? (
                  <Typography className={classes.emptyText}>
                    <SbuxTranslate>{"No values defined."}</SbuxTranslate>
                  </Typography>
                ) : (
                  <Paper variant="outlined" className={classes.scrollableList}>
                    {(slotTypeDetail.slotTypeValues ?? []).map((entry, i) => (
                      <Box key={i} className={classes.slotTypeValueEntry}>
                        <Typography className={classes.slotTypeValueName}>
                          {entry.sampleValue?.value}
                        </Typography>
                        {entry.synonyms && entry.synonyms.length > 0 && (
                          <Box>
                            {entry.synonyms.map((syn, j) => (
                              <Typography
                                key={j}
                                className={classes.synonymLine}
                              >
                                {syn.value}
                              </Typography>
                            ))}
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Paper>
                )}
              </>
            )}
          </LexSection>

          {/* Section 7: Missed Utterances */}
          <LexSection title="Missed Utterances">
            {missedUtterancesLoading ? (
              <SbuxLoader />
            ) : missedUtterancesError ? (
              <Typography className={classes.emptyText}>
                <SbuxTranslate>
                  {"Failed to load missed utterances."}
                </SbuxTranslate>
              </Typography>
            ) : missedUtterances.length === 0 ? (
              <Typography className={classes.emptyText}>
                <SbuxTranslate>
                  {"No missed utterances in the lookback period."}
                </SbuxTranslate>
              </Typography>
            ) : (
              <LexDataTable
                containerClass={classes.compactTableContainer}
                columns={["Utterance", "Count"]}
              >
                {missedUtterances.map((item) => (
                  <TableRow key={item.utterance || "__empty__"} hover>
                    <TableCell className={classes.compactTableCell}>
                      {item.utterance === EMPTY_UTTERANCE_SENTINEL ? (
                        <em>(empty utterance)</em>
                      ) : (
                        item.utterance || "-"
                      )}
                    </TableCell>
                    <TableCell className={classes.compactTableCell}>
                      {item.count}
                    </TableCell>
                  </TableRow>
                ))}
              </LexDataTable>
            )}
          </LexSection>
        </Box>
      </DialogContent>

      <DialogActions>
        <Box className="actionContainer">
          <Button variant="outlined" size="medium" onClick={handleClose}>
            <SbuxTranslate>{"Cancel"}</SbuxTranslate>
          </Button>
        </Box>
      </DialogActions>
    </SbuxDialog2>
  );
};

export default LexBotDetailModal;
